/**
 * @fileoverview Single-flight access-token refresh: concurrent callers (a burst of 401s, a socket
 * re-authenticating) share one refresh request and its outcome.
 */

/** The I/O and session side effects a refresh runs through. */
export interface AccessTokenRefreshHooks {
  /** Asks the server for a new access token; null when the response carried none. */
  readonly requestToken: () => Promise<string | null>;
  /** Stores the newly issued token. */
  readonly onRefreshed: (token: string) => void;
  /** Ends the session after the refresh failed. */
  readonly onFailed: () => void;
}

/** Runs at most one access-token refresh at a time. */
export class AccessTokenRefresher {
  private inFlight: Promise<string> | null = null;

  constructor(private readonly hooks: AccessTokenRefreshHooks) {}

  /** True while a refresh request is outstanding. */
  get isRefreshing(): boolean {
    return this.inFlight !== null;
  }

  /**
   * Resolves the new access token, joining the refresh already in flight if there is one.
   * Rejects, after `onFailed` ran once, when the refresh fails or returns no token.
   */
  refresh(): Promise<string> {
    if (!this.inFlight) {
      this.inFlight = this.run().finally(() => {
        this.inFlight = null;
      });
    }
    return this.inFlight;
  }

  private async run(): Promise<string> {
    try {
      const token = await this.hooks.requestToken();
      if (!token) {
        throw new Error('No access token received from refresh');
      }
      this.hooks.onRefreshed(token);
      return token;
    } catch (error) {
      this.hooks.onFailed();
      throw error;
    }
  }
}
